import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBackClose } from '../backStack';

// Someone's photo, large (brief 029). Call openPhoto({ src, name }) from anywhere;
// <PhotoViewerHost/>, mounted once with the toasts, draws it.

let listener = null;
export const openPhoto = ({ src, name }) => { if (src && listener) listener({ src, name: name || '' }); };

export function PhotoViewerHost() {
  const [photo, setPhoto] = useState(null);
  useEffect(() => { listener = setPhoto; return () => { if (listener === setPhoto) listener = null; }; }, []);
  const close = useCallback(() => setPhoto(null), []);
  return photo ? <PhotoViewer key={photo.src} photo={photo} onClose={close} /> : null;
}

function PhotoViewer({ photo, onClose }) {
  const closeRef = useRef(null);
  const [natural, setNatural] = useState(null);
  const [dy, setDy] = useState(0);
  const drag = useRef(null);

  // Back (and the iPhone's edge swipe) closes it.
  useBackClose(true, onClose);

  useEffect(() => {
    closeRef.current?.focus();
    // Captured first, so Escape closes only the photo, not a sheet underneath.
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey, true); document.body.style.overflow = overflow; };
  }, [onClose]);

  // Swipe down to close.
  const onTouchStart = e => { if (e.touches.length === 1) drag.current = { y: e.touches[0].clientY, x: e.touches[0].clientX }; };
  const onTouchMove = e => {
    if (!drag.current) return;
    const d = e.touches[0].clientY - drag.current.y;
    if (d < 0 || Math.abs(e.touches[0].clientX - drag.current.x) > d) { drag.current = null; setDy(0); return; }
    setDy(d);
  };
  const onTouchEnd = () => { if (!drag.current) return; drag.current = null; if (dy > 90) onClose(); else setDy(0); };

  return (
    <div className="photo-viewer" role="dialog" aria-modal="true" aria-label={`${photo.name}'s photo`} onClick={onClose}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      <button ref={closeRef} type="button" className="photo-viewer-close" onClick={onClose} aria-label="Close photo">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>
      <figure className="photo-viewer-figure" style={dy ? { transform: `translateY(${dy}px)`, opacity: Math.max(0.4, 1 - dy / 400) } : undefined}>
        {/* A 400 px photo isn't blown up past its own size. */}
        <img src={photo.src} alt={photo.name} onClick={e => e.stopPropagation()} onLoad={e => setNatural(e.currentTarget.naturalWidth)}
          style={natural ? { maxWidth: natural } : undefined} />
        {photo.name && <figcaption onClick={e => e.stopPropagation()}>{photo.name}</figcaption>}
      </figure>
    </div>
  );
}
